import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import * as orgApi from '../api/organizations';
import Modal from '../components/Modal';
import { useConfirm } from '../lib/confirm';
import { useToast } from '../components/toast';
import {
  Spinner,
  ErrorAlert,
  EmptyState,
  StatusPill,
  SelectField,
  UserSearchPicker,
  formatDate,
} from '../components/ui';
import { ui, cn } from '../lib/ui';

export default function OrgDetail() {
  const { orgId } = useParams();
  const { user } = useAuth();
  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const confirm = useConfirm();
  const toast = useToast();

  const isPlatformAdmin = user?.platformRole === 'platform-admin';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [orgData, memberData] = await Promise.all([
        orgApi.getOrg(orgId),
        orgApi.listMembers(orgId, { limit: 100 }),
      ]);
      setOrg(orgData.organization || orgData);
      setMembers(memberData.members || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  async function handleRemoveMember(member) {
    const name = member.user?.firstName
      ? `${member.user.firstName} ${member.user.lastName || ''}`
      : member.userId;
    const ok = await confirm({
      title: `Remove ${name}?`,
      message: 'This member will lose access to the organization.',
      danger: true,
    });
    if (!ok) return;
    try {
      await orgApi.removeMember(orgId, member.userId);
      toast.success('Member removed');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function handleRoleChange(member, newRole) {
    try {
      await orgApi.updateMemberRole(orgId, member.userId, newRole);
      toast.success('Role updated');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function handleAddMember(picked) {
    try {
      await orgApi.addMember(orgId, picked.userId);
      toast.success('Member added');
      setAddMemberOpen(false);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  }

  if (loading) return <Spinner label="Loading organization..." />;
  if (error) return <ErrorAlert error={error} />;
  if (!org) return <EmptyState>Organization not found.</EmptyState>;

  return (
    <>
      <Link to="/platform/admin" className={cn(ui.muted, 'text-body-s')}>&larr; Platform Admin</Link>
      <div className={cn(ui.row, ui.spread, 'mt-2 mb-4')}>
        <h1 className="m-0 text-title-l">{org.name}</h1>
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill status={org.status} />
          <StatusPill status={org.tier} />
        </div>
      </div>

      <div className={cn(ui.card, 'mb-6 p-4!')}>
        <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
          <dt className={cn(ui.muted, 'text-body-s')}>Slug</dt>
          <dd><code>{org.slug}</code></dd>
          <dt className={cn(ui.muted, 'text-body-s')}>Visibility</dt>
          <dd>{org.visibility}</dd>
          <dt className={cn(ui.muted, 'text-body-s')}>Members</dt>
          <dd>{org.memberCount ?? 0}</dd>
          <dt className={cn(ui.muted, 'text-body-s')}>Created</dt>
          <dd>{formatDate(org.createdAt)}</dd>
          {org.description && (
            <>
              <dt className={cn(ui.muted, 'text-body-s')}>Description</dt>
              <dd>{org.description}</dd>
            </>
          )}
        </dl>
      </div>

      <div className={cn(ui.row, ui.spread, 'mb-3')}>
        <h2 className="m-0 text-title-m">Members ({members.length})</h2>
        {isPlatformAdmin && (
          <button className={ui.btnPrimary} onClick={() => setAddMemberOpen(true)}>
            Add member
          </button>
        )}
      </div>

      {members.length === 0 ? (
        <EmptyState>No members.</EmptyState>
      ) : (
        <div className={cn(ui.card, ui.tableWrap)}>
          <table className={ui.table}>
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Status</th>
                <th>Joined</th>
                {isPlatformAdmin && (
                  <th className="text-right!">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {members.map(m => (
                <tr key={m.userId}>
                  <td>
                    {m.user
                      ? `${m.user.firstName || ''} ${m.user.lastName || ''}`.trim() || m.user.email || m.userId
                      : m.userId}
                  </td>
                  <td>
                    {isPlatformAdmin ? (
                      <SelectField
                        label=""
                        value={m.role}
                        onChange={e => handleRoleChange(m, e.target.value)}
                        style={{ margin: 0, minWidth: '8rem' }}
                      >
                        <option value="org-admin">org-admin</option>
                        <option value="org-moderator">org-moderator</option>
                        <option value="member">member</option>
                      </SelectField>
                    ) : (
                      m.role
                    )}
                  </td>
                  <td><StatusPill status={m.status} /></td>
                  <td>{formatDate(m.joinedAt)}</td>
                  {isPlatformAdmin && (
                    <td className="text-right!">
                      <button
                        className={cn(ui.btnDanger, ui.btnSm)}
                        onClick={() => handleRemoveMember(m)}
                      >
                        Remove
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={addMemberOpen}
        onClose={() => setAddMemberOpen(false)}
        title="Add Member"
      >
        <p className={cn('text-body-s', ui.muted)}>Search for a user by email to add them to this organization.</p>
        <UserSearchPicker
          onPick={handleAddMember}
          exclude={members.map(m => m.userId)}
        />
      </Modal>
    </>
  );
}
